import { apiFetch } from "./client";

/** The three groups of the template gallery. */
export type DocumentTemplateKind = "SALES_CONTRACT" | "PURCHASE_CONTRACT" | "GOODS_DOCUMENT";

/** A Word template (.docx) of the gallery. */
export type DocumentTemplate = {
  id: string;
  kind: DocumentTemplateKind;
  name: string;
  description: string | null;
  filename: string;
  sizeBytes: number;
  active: boolean;
  updatedAt: string;
};

/** A template a form offers to make a document from. */
export type DocumentTemplateOption = { id: string; name: string; description: string | null };

/** The fields a template of a kind can carry: the document's own, and a line's, repeated per line. */
export type DocumentTemplateFields = { kind: DocumentTemplateKind; document: string[]; line: string[] };

async function multipart<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(`/api${path}`, { method: "POST", credentials: "include", body: form });
  if (!res.ok) {
    let body: { code?: string; message?: string } = {};
    try { body = (await res.json()) as { code?: string; message?: string }; } catch { /* the body is not JSON */ }
    throw { status: res.status, code: body.code ?? `HTTP_${res.status}`, message: body.message ?? res.statusText };
  }
  return res.json() as Promise<T>;
}

export const documentTemplatesApi = {
  list: () => apiFetch<DocumentTemplate[]>("/admin/document-templates"),
  fields: () => apiFetch<DocumentTemplateFields[]>("/admin/document-templates/fields"),
  create: (kind: DocumentTemplateKind, name: string, description: string, file: File) => {
    const form = new FormData();
    form.append("kind", kind);
    form.append("name", name);
    if (description.trim() !== "") form.append("description", description);
    form.append("file", file);
    return multipart<DocumentTemplate>("/admin/document-templates", form);
  },
  update: (id: string, body: { name?: string; description?: string; active?: boolean }) =>
    apiFetch<DocumentTemplate>(`/admin/document-templates/${id}`, { method: "PUT", body }),
  replaceFile: (id: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return multipart<DocumentTemplate>(`/admin/document-templates/${id}/file`, form);
  },
  downloadUrl: (id: string) => apiFetch<{ url: string }>(`/admin/document-templates/${id}/download-url`),
};
